import { getTypeMetadataQuickFetches, makePropertyMetadataEntriesFetchKey, makePropertyMetadataEntriesFetchUrl, makeTypeMetadataEntriesFetchUrl, takeTypeMetadataQuickFetch } from 'framework-data-schema-quick-fetch';
import { withQuickFetch } from '../quickFetch/withQuickFetch';
import { keyEntriesByAppSettings } from './keyEntriesByAppSettings';
import { batchScheduler } from 'batch-promise';
import { unwrapResult } from '../utils/unwrapResult';

/**
 * This function actually makes our fetch request! It has a small optimization to avoid
 * checking for empty arrays higher up.
 * @param options.appSettingNames A list of app settings to query
 * @param options.httpClient The http client to use for the request
 * @returns
 */
const maybeFetchTypeEntries = ({
  appSettingNames,
  httpClient
}) => {
  if (appSettingNames.length === 0) {
    return Promise.resolve({});
  }
  return httpClient.post(makeTypeMetadataEntriesFetchUrl(), {
    data: appSettingNames
  }).then(keyEntriesByAppSettings);
};

/**
 * This function maps all app settings to a promise that can come from a quick fetch (if available)
 * or a batch fetch. It then waits for all promises to resolve and combines the results into a single object.
 * @param opts.appSettingNames A list of app settings to query
 * @param opts.httpClient The http client to use for the request
 * @returns All app settings results merged into a single object. Failures are ignored
 */
const fetchTypeMetadataEntriesWithQuickFetch = async ({
  appSettingNames,
  httpClient
}) => {
  const quickFetches = getTypeMetadataQuickFetches();
  const batchFetch = maybeFetchTypeEntries({
    httpClient,
    appSettingNames: appSettingNames.filter(appSettingName => !quickFetches.has(appSettingName))
  });
  const results = await Promise.allSettled(appSettingNames.map(appSettingName => {
    const quickFetched = takeTypeMetadataQuickFetch(appSettingName);
    return (!quickFetched ? batchFetch : quickFetched.catch(() => maybeFetchTypeEntries({
      httpClient,
      appSettingNames: [appSettingName]
    }))).then(result => result[appSettingName]);
  }));

  // Combine all the results into a single object
  return Object.fromEntries(appSettingNames.map((name, index) => [name, results[index]]));
};
export const batchedTypeMetadataRequest = batchScheduler().read().identity(req => req.appSettingName).partition(({
  httpClient
}) => httpClient).execute((requestMap, client) => fetchTypeMetadataEntriesWithQuickFetch({
  appSettingNames: Object.keys(requestMap),
  httpClient: client
})).extract(({
  response,
  identity
}) => response[identity] ? unwrapResult(response[identity]) : null).window(10);
export const fetchPropertyMetadataEntries = ({
  frameworkTypeIdentifier,
  appSettingName,
  httpClient
}) => withQuickFetch({
  requestName: makePropertyMetadataEntriesFetchKey({
    frameworkTypeIdentifier,
    appSettingName
  }),
  baseFetch: () => httpClient.get(makePropertyMetadataEntriesFetchUrl({
    frameworkTypeIdentifier,
    appSettingName
  }))
});